"use client";

import * as React from "react";
import * as SwitchPrimitives from "@radix-ui/react-switch";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const switchVariants = cva(
    "peer relative inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
    {
        variants: {
            size: {
                default: "h-6 w-11",
                sm: "h-4 w-8",
            },
        },
        defaultVariants: {
            size: "default",
        },
    }
);

export interface SwitchProps
    extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>,
        VariantProps<typeof switchVariants> {}

const Switch = React.forwardRef<
    React.ElementRef<typeof SwitchPrimitives.Root>,
    SwitchProps
>(({ className, size = "default", ...props }, ref) => (
    <SwitchPrimitives.Root
        className={cn(switchVariants({ size }), className)}
        {...props}
        ref={ref}
    >
        {/* Positioned via logical inset (`start-*`), NOT translate-x with the
            `rtl:` variant: `rtl:` matches on ANY ancestor's dir, while layout
            follows the NEAREST dir — inside a dir island the two disagree and
            the thumb escapes the track. Logical inset always follows the
            switch's own computed direction. */}
        <SwitchPrimitives.Thumb
            className={cn(
                "pointer-events-none absolute start-0 top-0 block rounded-full bg-background shadow-lg ring-0 transition-[inset-inline-start]",
                {
                    "h-5 w-5 data-[state=checked]:start-5":
                        size === "default",
                    "h-3 w-3 data-[state=checked]:start-4": size === "sm",
                }
            )}
        />
    </SwitchPrimitives.Root>
));
Switch.displayName = "Switch";

export { Switch, switchVariants };
